import { diagnosticBytes, safeId, sanitizeDiagnosticEvent } from "./runtime-diagnostics-schema";
const KEY = "runtime-diagnostics-outbox-v1";
const PATH = "/nri/api/runtime/diagnostics/reports";
const DAY = 86400000;
const isUuid = value =>
  typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const isoDate = value =>
  typeof value === "string" &&
  /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) &&
  Number.isFinite(Date.parse(value));

function cleanReport(input, now) {
  try {
    // Read each untrusted property once, so getters cannot change it after validation.
    input = {
      schemaVersion: input?.schemaVersion,
      reportId: input?.reportId,
      diagnosticSessionId: input?.diagnosticSessionId,
      incidentId: input?.incidentId,
      runtimeRoomId: input?.runtimeRoomId,
      phase: input?.phase,
      startedAt: input?.startedAt,
      endedAt: input?.endedAt,
      events: input?.events
    };

    if (
      input?.schemaVersion !== 1 ||
      !isUuid(input.reportId) ||
      !isUuid(input.diagnosticSessionId) ||
      !isUuid(input.incidentId) ||
      !safeId(input.runtimeRoomId) ||
      !["ongoing", "recovered", "failed"].includes(input.phase) ||
      !isoDate(input.startedAt) ||
      now - Date.parse(input.startedAt) > DAY ||
      Date.parse(input.startedAt) > now + 60000 ||
      !Array.isArray(input.events)
    )
      return null;
    const events = [];
    for (const source of input.events.slice(-200)) {
      const item = {
        event: source?.event,
        data: source?.data,
        at: source?.at,
        sequence: source?.sequence,
        attemptId: source?.attemptId
      };
      const data = sanitizeDiagnosticEvent(item.event, item.data);
      if (
        data === null ||
        !isoDate(item.at) ||
        now - Date.parse(item.at) > DAY ||
        Date.parse(item.at) > now + 60000 ||
        !Number.isSafeInteger(item.sequence) ||
        item.sequence < 1
      )
        continue;
      events.push({
        sequence: item.sequence,
        at: item.at,
        event: item.event,
        ...(isUuid(item.attemptId) ? { attemptId: item.attemptId } : {}),
        data
      });
    }
    const report = {
      schemaVersion: 1,
      reportId: input.reportId,
      diagnosticSessionId: input.diagnosticSessionId,
      incidentId: input.incidentId,
      runtimeRoomId: input.runtimeRoomId,
      phase: input.phase,
      startedAt: input.startedAt,
      ...(isoDate(input.endedAt) ? { endedAt: input.endedAt } : {}),
      events
    };
    while (report.events.length && diagnosticBytes(report) > 24576) report.events.shift();
    return diagnosticBytes(report) <= 24576 ? report : null;
  } catch (_) {
    return null;
  }
}

function capability(input, base, now, tenantHost) {
  try {
    input = {
      endpoint: input?.endpoint,
      sessionId: input?.sessionId,
      token: input?.token,
      expiresAt: input?.expiresAt
    };
    if (typeof input.endpoint !== "string") return null;

    const origin = new URL(base);
    const endpoint = new URL(input?.endpoint, origin);
    if (
      !["https:", "http:"].includes(origin.protocol) ||
      endpoint.origin !== origin.origin ||
      endpoint.pathname !== PATH ||
      endpoint.search ||
      endpoint.hash ||
      endpoint.username ||
      endpoint.password ||
      !isUuid(input?.sessionId) ||
      typeof input.token !== "string" ||
      !/^[A-Za-z0-9_.-]{1,4096}$/.test(input.token) ||
      !isoDate(input.expiresAt) ||
      Date.parse(input.expiresAt) <= now ||
      Date.parse(input.expiresAt) > now + DAY + 60000
    )
      return null;
    return {
      sessionId: input.sessionId,
      token: input.token,
      expiresAt: input.expiresAt,
      endpoint: endpoint.href,
      ...(typeof tenantHost === "string" && /^[A-Za-z0-9.-]{1,253}$/.test(tenantHost) ? { tenantHost } : {})
    };
  } catch (_) {
    return null;
  }
}

export function createRuntimeDiagnosticsTransport(options = {}) {
  const now = options.now || Date.now;
  const schedule = options.schedule || setTimeout;
  const cancel = options.cancel || clearTimeout;
  const random = options.random || Math.random;
  const network = options.fetch || ((...args) => fetch(...args));
  let storage = options.storage;
  try {
    if (!storage && typeof window !== "undefined") storage = window.localStorage;
  } catch (_) {
    /* Storage may be denied. */
  }
  let queue = [],
    attempts = [],
    configured,
    busy = false,
    wake,
    loaded = false;
  function persist() {
    try {
      queue = queue.filter(item => item.expires > now() && item.tries < 3);
      attempts = attempts.filter(at => at > now() - 600000).slice(-6);
      while (diagnosticBytes({ queue, attempts }) > 131072) queue.shift();
      storage?.setItem(KEY, JSON.stringify({ queue, attempts }));
    } catch (_) {
      /* Quota and privacy modes cannot affect RTC. */
    }
  }
  function later(ms) {
    cancel(wake);
    wake = schedule(() => {
      // eslint-disable-next-line no-use-before-define
      flush();
    }, Math.max(1, ms));
    wake?.unref?.();
  }
  function flush(keepalive = false) {
    try {
      persist();
      if (busy || !queue.length) return;
      const item = queue[0];
      if (attempts.length >= 6) {
        later(attempts[0] + 600000 - now() + 1);
        return;
      }
      if (item.next > now()) {
        later(item.next - now());
        return;
      }
      busy = true;
      attempts.push(now());
      item.tries++;
      persist();
      const controller = new AbortController();
      let timedOut = false;
      const timeout = schedule(() => {
        timedOut = true;
        controller.abort();
      }, 5000);
      timeout?.unref?.();
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${item.cap.token}` };
      if (item.cap.tenantHost) headers["X-Tenant-Host"] = item.cap.tenantHost;
      Promise.resolve()
        .then(() =>
          network(item.cap.endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify(item.report),
            credentials: "omit",
            redirect: "error",
            signal: controller.signal,
            keepalive
          })
        )
        .then(response => {
          if (response.status === 202 || (response.status >= 400 && response.status < 500 && response.status !== 429))
            item.tries = 3;
          else {
            const retry = response.headers?.get?.("Retry-After");
            const wait = /^\d+$/.test(retry || "") ? Number(retry) * 1000 : Date.parse(retry) - now();
            item.next =
              now() + Math.max(1000 + Math.floor(random() * 1000), Number.isFinite(wait) ? Math.min(DAY, wait) : 0);
          }
        })
        .catch(() => {
          item.next = now() + (timedOut ? 5000 : 1000) + Math.floor(random() * 1000);
        })
        .finally(() => {
          cancel(timeout);
          busy = false;
          persist();
          later(1);
        })
        .catch(() => {});
    } catch (_) {
      /* Fail closed, no recursive telemetry or UI errors. */
    }
  }
  return {
    configure(input, base, tenantHost) {
      try {
        configured = capability(input, base, now(), tenantHost);
        if (!configured) return;
        if (!loaded) {
          loaded = true;
          try {
            const saved = storage?.getItem(KEY);
            if (saved && saved.length <= 131072) {
              const parsed = JSON.parse(saved);
              attempts = Array.isArray(parsed.attempts)
                ? parsed.attempts.filter(at => Number.isFinite(at) && at <= now() && at > now() - 600000).slice(-6)
                : [];
              for (const item of (Array.isArray(parsed.queue) ? parsed.queue : []).slice(-128)) {
                const cap = capability(item.cap, base, now(), item.cap?.tenantHost);
                const report = cleanReport(item.report, now());
                if (
                  cap &&
                  report &&
                  cap.sessionId === report.diagnosticSessionId &&
                  Number.isInteger(item.tries) &&
                  item.tries >= 0 &&
                  item.tries < 3 &&
                  Number.isFinite(item.expires) &&
                  item.expires > now() &&
                  item.expires <= now() + DAY
                )
                  queue.push({
                    cap,
                    report,
                    tries: item.tries,
                    expires: Math.min(item.expires, Date.parse(cap.expiresAt)),
                    next: Number.isFinite(item.next) ? Math.min(item.next, now() + DAY) : now()
                  });
              }
            }
          } catch (_) {
            /* Corrupt storage discarded. */
          }
        }
        flush();
      } catch (_) {
        /* Optional descriptor. */
      }
    },
    enqueue(input, keepalive = false) {
      try {
        if (!configured || Date.parse(configured.expiresAt) <= now()) return;
        const report = cleanReport(input, now());
        if (
          !report ||
          report.diagnosticSessionId !== configured.sessionId ||
          queue.some(item => item.report.reportId === report.reportId)
        )
          return;
        queue.push({
          report,
          cap: configured,
          expires: Math.min(now() + DAY, Date.parse(configured.expiresAt)),
          tries: 0,
          next: now()
        });
        persist();
        flush(keepalive);
      } catch (_) {
        /* Never propagate reporting failure. */
      }
    },
    flush
  };
}
